import type { IModuleI18n, IModuleStrings, Lang, ModuleStringFallback } from '@platform/core/types';
import { localized } from './localized';

/** Непорожній рядок пакета або undefined (значення іншого типу чи порожні рядки вважаються відсутніми). */
const text = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined);

/** Підпис із визначення (готовий рядок — як є, локалізований текст — мовою інтерфейсу), інакше — ключ або значення. */
function fallbackText(fallback: ModuleStringFallback, lang: Lang, value: string): string {
  const own = typeof fallback === 'string' ? fallback : localized(fallback, lang);
  return own || value;
}

/**
 * Резолвер рядків модуля (ADR-012): навігує об'єкт пакета `module.<ключ>` (не через t() — значення варіантів і статусів
 * містять '.', ':' чи пробіли), а без рядка повертає підпис визначення мовою інтерфейсу або сам ключ/значення.
 * `bundle` читається ліниво при кожному виклику, тож рядки, додані loadModule після створення резолвера, видно одразу.
 */
export function createModuleStrings(moduleKey: string, lang: Lang, bundle: () => IModuleI18n | undefined): IModuleStrings {
  const entityOf = (rtKey: string) => bundle()?.entities?.[rtKey];
  const fieldOf = (rtKey: string, fieldKey: string) => entityOf(rtKey)?.fields?.[fieldKey];
  const optionText = (rtKey: string, fieldKey: string, value: string) => text(fieldOf(rtKey, fieldKey)?.options?.[value]);
  const statusText = (rtKey: string, value: string) => text(entityOf(rtKey)?.statuses?.[value]);
  const pick = (found: string | undefined, fallback: ModuleStringFallback, value = ''): string => found ?? fallbackText(fallback, lang, value);
  return {
    moduleKey,
    lang,
    moduleName: (fallback) => pick(text(bundle()?.name), fallback),
    tagline: (fallback) => pick(text(bundle()?.tagline), fallback),
    entity: (rtKey, part, fallback) => pick(text(entityOf(rtKey)?.[part]), fallback),
    field: (rtKey, fieldKey, fallback) => pick(text(fieldOf(rtKey, fieldKey)?.label), fallback, fieldKey),
    option: (rtKey, fieldKey, value, fallback) =>
      pick(optionText(rtKey, fieldKey, value) ?? (fieldKey === 'status' ? statusText(rtKey, value) : undefined), fallback, value),
    status: (rtKey, value, fallback) => pick(statusText(rtKey, value) ?? optionText(rtKey, 'status', value), fallback, value),
    menuGroup: (rtKey, groupEn, fallback) => pick(text(entityOf(rtKey)?.menu?.groups?.[groupEn]), fallback, groupEn),
    menuItem: (rtKey, labelEn, fallback) => pick(text(entityOf(rtKey)?.menu?.items?.[labelEn]), fallback, labelEn),
    formGroup: (rtKey, groupId, fallback) => pick(text(entityOf(rtKey)?.form?.groups?.[groupId]), fallback, groupId),
    related: (rtKey, relatedKey, fallback) => pick(text(entityOf(rtKey)?.related?.[relatedKey]), fallback, relatedKey),
    bi: (titleEn, fallback) => pick(text(bundle()?.bi?.[titleEn]), fallback, titleEn),
    page: (pageKey, fallback) => pick(text(bundle()?.pages?.[pageKey]), fallback, pageKey),
    feature: (index, part, fallback) => pick(text(bundle()?.features?.[String(index)]?.[part]), fallback),
    useCase: (slug, fallback) => pick(text(bundle()?.useCases?.[slug]), fallback, slug),
    motivation: (fallback) => pick(text(bundle()?.motivation), fallback),
  };
}
